import React, {
  createContext,
  useContext,
  useReducer,
  useEffect,
  useCallback,
  ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';
import { knowledgeBaseService, KnowledgeBase } from '../services/knowledgeBaseService';
import { getErrorKey } from '../lib/errors';

interface KnowledgeBaseState {
  knowledgeBases: KnowledgeBase[];
  loading: boolean;
  error: string | null;
  selectedKnowledgeBases: Set<string>;
  globalFilter: 'all' | 'recent' | 'default' | 'inactive';
}

type KnowledgeBaseAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_KNOWLEDGE_BASES'; payload: KnowledgeBase[] }
  | { type: 'TOGGLE_KNOWLEDGE_BASE'; payload: string }
  | { type: 'SELECT_ALL_KNOWLEDGE_BASES' }
  | { type: 'CLEAR_SELECTED_KNOWLEDGE_BASES' }
  | { type: 'SET_SELECTED_KNOWLEDGE_BASES'; payload: string[] }
  | { type: 'SET_GLOBAL_FILTER'; payload: 'all' | 'recent' | 'default' | 'inactive' };

const initialState: KnowledgeBaseState = {
  knowledgeBases: [],
  loading: false,
  error: null,
  selectedKnowledgeBases: new Set(),
  globalFilter: 'all',
};

function knowledgeBaseReducer(
  state: KnowledgeBaseState,
  action: KnowledgeBaseAction
): KnowledgeBaseState {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload, loading: false };
    case 'SET_KNOWLEDGE_BASES':
      return { ...state, knowledgeBases: action.payload, loading: false, error: null };
    case 'TOGGLE_KNOWLEDGE_BASE':
      const newSelected = new Set(state.selectedKnowledgeBases);
      if (newSelected.has(action.payload)) {
        newSelected.delete(action.payload);
      } else {
        newSelected.add(action.payload);
      }
      return { ...state, selectedKnowledgeBases: newSelected };
    case 'SELECT_ALL_KNOWLEDGE_BASES':
      return {
        ...state,
        selectedKnowledgeBases: new Set(state.knowledgeBases.map(kb => kb.id)),
      };
    case 'CLEAR_SELECTED_KNOWLEDGE_BASES':
      return { ...state, selectedKnowledgeBases: new Set() };
    case 'SET_SELECTED_KNOWLEDGE_BASES':
      return { ...state, selectedKnowledgeBases: new Set(action.payload) };
    case 'SET_GLOBAL_FILTER':
      return { ...state, globalFilter: action.payload };
    default:
      return state;
  }
}

interface KnowledgeBaseContextType {
  state: KnowledgeBaseState;
  fetchKnowledgeBases: () => Promise<void>;
  fetchInactiveKnowledgeBases: () => Promise<void>;
  fetchAllKnowledgeBases: () => Promise<void>;
  toggleKnowledgeBase: (id: string) => void;
  selectAllKnowledgeBases: () => void;
  clearSelectedKnowledgeBases: () => void;
  setSelectedKnowledgeBases: (ids: string[]) => void;
  getSelectedKnowledgeBases: () => KnowledgeBase[];
  setGlobalFilter: (filter: 'all' | 'recent' | 'default' | 'inactive') => void;
}

const KnowledgeBaseContext = createContext<KnowledgeBaseContextType | undefined>(undefined);

export const useKnowledgeBase = () => {
  const context = useContext(KnowledgeBaseContext);
  if (!context) {
    throw new Error('useKnowledgeBase must be used within a KnowledgeBaseProvider');
  }
  return context;
};

interface KnowledgeBaseProviderProps {
  children: ReactNode;
}

export const KnowledgeBaseProvider: React.FC<KnowledgeBaseProviderProps> = ({ children }) => {
  const { t } = useTranslation();
  const [state, dispatch] = useReducer(knowledgeBaseReducer, initialState);

  const fetchKnowledgeBases = useCallback(async () => {
    dispatch({ type: 'SET_LOADING', payload: true });
    dispatch({ type: 'SET_ERROR', payload: null }); // Clear previous errors

    try {
      const response = await knowledgeBaseService.getKnowledgeBasesPaginated({
        includes: {
          is_shared: true,
        },
      });
      // Ensure we always have an array, even if the API returns unexpected data
      const normalizedKnowledgeBases = Array.isArray(response.items) ? response.items : [];

      // Debug: Log the response to see what we're getting
      console.log('Knowledge bases API response:', response);
      console.log('Knowledge bases items:', normalizedKnowledgeBases);
      console.log(
        'Sample KB with shares:',
        normalizedKnowledgeBases.find(kb => kb.shares && kb.shares.length > 0)
      );

      dispatch({ type: 'SET_KNOWLEDGE_BASES', payload: normalizedKnowledgeBases });
    } catch (error) {
      console.error('Failed to fetch knowledge bases:', error);
      const errorKey = getErrorKey(error);
      const errorMessage = t(errorKey);

      dispatch({
        type: 'SET_ERROR',
        payload: errorMessage,
      });
      // Set empty array on error to prevent runtime issues
      dispatch({ type: 'SET_KNOWLEDGE_BASES', payload: [] });
    }
  }, [t]); // Empty dependency array since it doesn't depend on any external values

  const fetchInactiveKnowledgeBases = useCallback(async () => {
    dispatch({ type: 'SET_LOADING', payload: true });
    dispatch({ type: 'SET_ERROR', payload: null });

    try {
      const response = await knowledgeBaseService.getKnowledgeBasesPaginated({
        includes: {
          is_shared: true,
        },
      });
      // Filter for inactive knowledge bases
      const normalizedKnowledgeBases = Array.isArray(response.items)
        ? response.items.filter((kb: KnowledgeBase) => !kb.is_active)
        : [];
      dispatch({ type: 'SET_KNOWLEDGE_BASES', payload: normalizedKnowledgeBases });
    } catch (error) {
      console.error('Failed to fetch inactive knowledge bases:', error);
      const errorKey = getErrorKey(error);
      const errorMessage = t(errorKey);
      dispatch({
        type: 'SET_ERROR',
        payload: errorMessage,
      });
      dispatch({ type: 'SET_KNOWLEDGE_BASES', payload: [] });
    }
  }, [t]);

  const fetchAllKnowledgeBases = useCallback(async () => {
    dispatch({ type: 'SET_LOADING', payload: true });
    dispatch({ type: 'SET_ERROR', payload: null });

    try {
      // Fetch all KBs with shared ones included
      const response = await knowledgeBaseService.getKnowledgeBasesPaginated({
        includes: {
          is_shared: true,
        },
      });

      const normalizedKnowledgeBases = Array.isArray(response.items) ? response.items : [];
      dispatch({ type: 'SET_KNOWLEDGE_BASES', payload: normalizedKnowledgeBases });
    } catch (error) {
      console.error('Failed to fetch all knowledge bases:', error);
      const errorKey = getErrorKey(error);
      const errorMessage = t(errorKey);
      dispatch({
        type: 'SET_ERROR',
        payload: errorMessage,
      });
      dispatch({ type: 'SET_KNOWLEDGE_BASES', payload: [] });
    }
  }, [t]);

  const toggleKnowledgeBase = useCallback((id: string) => {
    dispatch({ type: 'TOGGLE_KNOWLEDGE_BASE', payload: id });
  }, []);

  const selectAllKnowledgeBases = useCallback(() => {
    dispatch({ type: 'SELECT_ALL_KNOWLEDGE_BASES' });
  }, []);

  const clearSelectedKnowledgeBases = useCallback(() => {
    dispatch({ type: 'CLEAR_SELECTED_KNOWLEDGE_BASES' });
  }, []);

  const setSelectedKnowledgeBases = useCallback((ids: string[]) => {
    dispatch({ type: 'SET_SELECTED_KNOWLEDGE_BASES', payload: ids });
  }, []);

  const getSelectedKnowledgeBases = useCallback(() => {
    return state.knowledgeBases.filter(kb => state.selectedKnowledgeBases.has(kb.id));
  }, [state.knowledgeBases, state.selectedKnowledgeBases]);

  const setGlobalFilter = useCallback((filter: 'all' | 'recent' | 'default' | 'inactive') => {
    dispatch({ type: 'SET_GLOBAL_FILTER', payload: filter });
  }, []);

  useEffect(() => {
    // AuthenticatedProviders ensures this only mounts inside ProtectedRoute
    // So we can safely call the API without auth checks
    fetchKnowledgeBases();
  }, [fetchKnowledgeBases]);

  return (
    <KnowledgeBaseContext.Provider
      value={{
        state,
        fetchKnowledgeBases,
        fetchInactiveKnowledgeBases,
        fetchAllKnowledgeBases,
        toggleKnowledgeBase,
        selectAllKnowledgeBases,
        clearSelectedKnowledgeBases,
        setSelectedKnowledgeBases,
        getSelectedKnowledgeBases,
        setGlobalFilter,
      }}
    >
      {children}
    </KnowledgeBaseContext.Provider>
  );
};

export default KnowledgeBaseContext;
